import type { FC } from 'react'

import { LoaderWrapper, Table } from '@skytech/ui'
import { getQueryFilter, getWhiteLabelForDealerStyle } from '@skytech/utils'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/App.context'
import ListLoader from '@/components/util/ListLoader'
import NoResults from '@/components/util/NoResults'
import { useSortParams } from '@/hooks/searchParams'

import { useTranslation } from '../i18n/hooks'

import { INITIAL_PER_PAGE_LIMIT, PER_PAGE_LIMIT } from './consts'
import { getIsOrderOpen } from './helpers'
import { queries } from './queries'
import type { OrderStatusCount, UpgradeOrder } from './resources'
import subscriptionOrderResources from './resources'
import { SubscriptionOrderFilters } from './SubscriptionOrderFilters'
import { SubscriptionOrderListItem } from './SubscriptionOrdersTable/SubscriptionOrderListItem'
import { SubscriptionOrdersTHead } from './SubscriptionOrdersTable/SubscriptionOrdersTHead'

export const SubscriptionOrderUpgradeList: FC<{
  textSearch: string
  status: string
  querySearch: string
  statusCounter: OrderStatusCount
  setTextSearch: (val: string) => void
}> = ({ textSearch, statusCounter, status, querySearch, setTextSearch }) => {
  const translate = useTranslation()
  const { whiteLabel } = useAppContext()
  const [{ querySort, querySortBy }] = useSortParams<keyof UpgradeOrder>()

  const upgradeQuery = useInfiniteQuery({
    queryKey: queries.upgradeSubscriptionOrders({
      keyword: querySearch,
      status,
      sortBy: querySortBy,
      sort: querySort,
    }),
    queryFn: ({ pageParam = 1 }) => {
      const limit = pageParam === 1 ? INITIAL_PER_PAGE_LIMIT : PER_PAGE_LIMIT
      const sort = !querySort || querySort === 'off' ? undefined : querySort
      return subscriptionOrderResources.getUpgradeList({
        offset: pageParam,
        limit,
        keyword: querySearch,
        status,
        sortBy: querySortBy ?? undefined,
        sort: sort,
      })
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.length, 0)
      if (lastPage.length < (allPages.length === 1 ? INITIAL_PER_PAGE_LIMIT : PER_PAGE_LIMIT)) {
        return undefined
      }
      return totalFetched + 1
    },
  })
  const orders = upgradeQuery.data?.pages.flat() || []
  const isLoaderVisible = Boolean(upgradeQuery.hasNextPage && !upgradeQuery.isFetchingNextPage)

  function subOrderStringMatcher(order: UpgradeOrder) {
    return [
      order.id,
      order.customer,
      order.user,
      order.code,
      translate(`STATUS_SUBSCRIPTION_ORDER/${order.subStatus}`),
      order.subscriptionServiceName,
    ]
  }
  const filteredOrders = orders.filter(order => {
    const statusFilter =
      status === 'all' ||
      (status === 'completed' && order.subStatus === 'done') || // Handle 'completed' as 'done'
      status === order.subStatus ||
      (status === 'open' && getIsOrderOpen(order.subStatus))
    const searchFilter = getQueryFilter(textSearch, subOrderStringMatcher)(order)
    return statusFilter && searchFilter
  })

  const { data: totalCount, isLoading: isTotalCountLoading } = useQuery(
    queries.upgradeSubscriptionOrdersTotalCount({
      keyword: querySearch,
      status,
    }),
    () =>
      subscriptionOrderResources.getTotalOrders({
        keyword: querySearch,
        status,
        orderType: 'upgrade',
      })
  )

  return (
    <>
      <SubscriptionOrderFilters
        statusCounter={statusCounter}
        totalCount={totalCount}
        isTotalCountLoading={isTotalCountLoading}
        textSearch={textSearch}
        status={status}
        setTextSearch={(val: string) => setTextSearch(val)}
      />
      <LoaderWrapper
        isLoading={upgradeQuery.isInitialLoading}
        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
      >
        <div className="max-w-full">
          <Table rowHeight="tall">
            {orders.length > 0 && <SubscriptionOrdersTHead />}

            <tbody>
              {filteredOrders.map(order => (
                <SubscriptionOrderListItem
                  isUpgrade
                  key={order.id}
                  {...order}
                  textSearch={textSearch}
                />
              ))}
            </tbody>
          </Table>

          {orders.length === 0 && <NoResults />}

          <ListLoader
            conditionSelector={() => isLoaderVisible}
            action={upgradeQuery.fetchNextPage}
          />
        </div>
      </LoaderWrapper>
    </>
  )
}
